/***************************************************************************/
"use client";
/***************************************************************************/

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LucideProps } from "lucide-react";
import * as React from "react";
import { cn } from "../../utils/css";
import DefaultLoadingIcon from "./loading-icon";

const buttonVariants = cva(
  "box-border inline-flex items-center justify-center whitespace-nowrap font-medium text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-noneX [&_.lucide]:stroke-2",
  {
    variants: {
      variant: {
        default:
          "border border-input bg-background text-foreground shadow-sm hover:bg-neutral-100",
        primary:
          "border border-primary bg-primary text-primary-foreground shadow hover:bg-primary/80",
        success:
          "border border-success bg-success text-success-foreground shadow-sm hover:bg-success/80",
        info: "border border-info bg-info text-info-foreground shadow-sm hover:bg-info/80",
        warning:
          "border border-warning bg-warning text-warning-foreground shadow-sm hover:bg-warning/80",
        error:
          "border border-error bg-error text-error-foreground shadow-sm hover:bg-error/80",
        secondary:
          "border border-gray-200/50 bg-gray-100 shadow-sm hover:border-gray-200/80 hover:bg-gray-200/80",
        outline:
          "border border-input bg-background text-foreground shadow-sm hover:bg-neutral-100",
        "outline-primary":
          "border bg-background border-primary text-primary shadow hover:bg-primary-50",
        "outline-success":
          "border bg-background border-success text-success shadow-sm hover:bg-success-50",
        "outline-info":
          "border bg-background border-info text-info shadow-sm hover:bg-info-50",
        "outline-warning":
          "border bg-background border-warning text-warning shadow-sm hover:bg-warning-50",
        "outline-error":
          "border bg-background border-error text-error shadow-sm hover:bg-error-50",
        ghost:
          "border border-[color:transparent] hover:border-neutral-50 hover:bg-neutral-100 shadow-none",
        link: "text-primary underline-offset-4 hover:underline shadow-none",
      },
      size: {
        default: "min-h-10 min-w-10 px-3 py-1.5 rounded gap-2",
        sm: "min-h-8 min-w-8 rounded px-2 py-1 text-sm rounded gap-1",
        lg: "min-h-11 min-w-11 text-lg rounded px-4 py-1.5 rounded-md gap-2",
      },
    },
    defaultVariants: {
      variant: "outline",
      size: "default",
    },
  },
);

const buttonIconVariants = cva("", {
  variants: {
    size: {
      default: "[&_.lucide]:h-4 [&_.lucide]:w-4",
      sm: "[&_.lucide]:h-3 [&_.lucide]:w-3",
      lg: "[&_.lucide]:h-5 [&_.lucide]:w-5",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const ButtonContext = React.createContext<{
  loading?: boolean;
  size?: "default" | "sm" | "lg" | null | undefined;
}>({ size: "default" });

export interface TButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, TButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading,
      children,
      type = "button",
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <ButtonContext.Provider value={{ loading, size }}>
        <Comp
          className={cn(
            buttonVariants({ variant, size, className }),
            loading
              ? "[&>*:not(.--loading)]:invisible relative justify-center items-center"
              : undefined,
          )}
          ref={ref}
          type={type}
          {...props}
        >
          {children}
          {loading && <ButtonLoadingIcon />}
        </Comp>
      </ButtonContext.Provider>
    );
  },
);
Button.displayName = "Button";

const ButtonIcon: React.FC<
  React.HTMLAttributes<HTMLSpanElement> &
    VariantProps<typeof buttonIconVariants>
> = ({ className, size, ...props }) => {
  const buttonContextValue = React.useContext(ButtonContext);
  const derivedSize = size ?? buttonContextValue.size;

  return (
    <span
      className={cn(buttonIconVariants({ size: derivedSize }), className)}
      {...props}
    />
  );
};

const ButtonLoadingIcon: React.FC<LucideProps> = ({ className, ...props }) => {
  return (
    <ButtonIcon className="absolute --loading">
      <DefaultLoadingIcon className={cn("--loading", className)} {...props} />
    </ButtonIcon>
  );
};

export {
  Button,
  ButtonContext,
  ButtonIcon,
  ButtonLoadingIcon,
  buttonIconVariants,
  buttonVariants,
};
